import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Trash2, Eye, FileText, Zap } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_BASE_URL } from "../../config/api";

const getScoreColor = (s) => s >= 7 ? "text-[#388E3C]" : s >= 4 ? "text-[#F57F17]" : "text-[#D32F2F]";

export default function BasicAssessmentList() {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await axios.get(`${BACKEND_BASE_URL}/api/basic/assessments`);
      setAssessments(res.data);
      const d = await axios.get(`${BACKEND_BASE_URL}/api/basic/drafts`); setDrafts(d.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_BASE_URL}/api/basic/assessments/${id}`);
      toast.success("Basic assessment deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 animate-spin text-[#43A047]" /></div>;

  return (
    <div className="p-8 max-w-5xl" data-testid="basic-assessment-list">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-4xl tracking-tight font-light text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>Basic Assessments</h1>
            <Badge className="bg-[#43A047] text-white text-[10px] tracking-wider"><Zap className="w-3 h-3 mr-1" />BASIC</Badge>
          </div>
          <p className="text-sm text-[#617364] mt-1">{assessments.length} Basic assessments</p>
        </div>
        <Button onClick={() => navigate("/basic/new")} className="bg-[#43A047] hover:bg-[#388E3C] text-white" data-testid="basic-new-btn">New Basic Assessment</Button>
      </div>

      {drafts.length > 0 && <div className="mb-6 space-y-2"><h2 className="font-semibold text-green-950">Saved drafts</h2>{drafts.map(d => <div key={d.id} className="p-4 rounded-xl bg-white border flex justify-between items-center"><span>{d.payload?.form?.product_name || 'Untitled Basic draft'}</span><Button variant="outline" onClick={() => navigate(`/basic/new?draft=${d.id}`)}>Continue draft</Button></div>)}</div>}
      {assessments.length === 0 ? (
        <Card className="border-[#E0E6E1] bg-white"><CardContent className="p-12 text-center"><FileText className="w-12 h-12 text-[#E0E6E1] mx-auto mb-4" /><p className="text-[#617364]">No Basic assessments yet.</p></CardContent></Card>
      ) : (
        <Card className="border-[#E0E6E1] bg-white"><CardContent className="p-0">
          <Table><TableHeader><TableRow className="border-[#E0E6E1]">
            <TableHead className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Product</TableHead>
            <TableHead className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Type</TableHead>
            <TableHead className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">TOSS</TableHead>
            <TableHead className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Date</TableHead>
            <TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {assessments.map(a => (
              <TableRow key={a.id} className="border-[#E0E6E1] hover:bg-[#F8F9F8]">
                <TableCell className="text-sm text-[#1B291D] font-medium">{a.product_name}<p className="text-xs text-slate-500">{a.results?.methodology_version?.includes("process-b") ? "Process B" : "Earlier method"}</p></TableCell>
                <TableCell><Badge variant="outline" className="border-[#E0E6E1] text-[#617364] text-xs">{a.product_type}</Badge></TableCell>
                <TableCell>{a.results?.upsi != null ? <span className={`text-lg font-medium ${getScoreColor(a.results.upsi)}`} style={{ fontFamily: 'Work Sans' }}>{(a.results.upsi * 10).toFixed(2) + " /100"}</span> : "N/A"}</TableCell>
                <TableCell className="text-sm text-[#617364]">{new Date(a.created_at).toLocaleDateString()}</TableCell>
                <TableCell><div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => navigate(`/basic/assessments/${a.id}`)} className="text-[#43A047]" data-testid={`basic-view-${a.id}`}><Eye className="w-4 h-4" /></Button><Button variant="ghost" size="sm" onClick={() => navigate(`/basic/new?copy=${a.id}`)}>Duplicate</Button><Button variant="ghost" size="sm" onClick={() => navigate(`/basic/new?revise=${a.id}`)}>Create revised version</Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(a.id)} className="text-[#D32F2F]" data-testid={`basic-del-${a.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div></TableCell>
              </TableRow>
            ))}
          </TableBody></Table>
        </CardContent></Card>
      )}
    </div>
  );
}
